import { getAnalytics } from "@tm/insurance/analytics";
import { navigationActions } from "@tm/insurance/navigation";
import React, { useEffect, useState } from "react";
import FormattedText, {
  getPlainText,
  TextNodeType,
} from "../FormattedText/FormattedText";
import MoreBenefits from "../InsurancePlans/MoreBenefits";
import { IBenefits } from "../InsurancePlans/type";
import DownloadPolicyModal from "./DownloadPolicyModal";
import { useNetPriceContext } from "./contexts/NetPriceContext";
import Icon, { IconData } from "./Icon";
import {
  ActionsSection,
  BenefitIcon,
  BenefitItem,
  BenefitsSection,
  BenefitText,
  BottomSection,
  CardContainer,
  CompanyName,
  CurrentPrice,
  DiscountText,
  DiscountWrapper,
  DownloadIcon,
  DownloadSection,
  LeftSection,
  LogoSection,
  MoreBenefitsSection,
  PerPersonText,
  PlanTitle,
  PriceContainer,
  RightSection,
  StrikethroughPrice,
  TitleSection,
  TopSection,
} from "./InsuranceListItem.style";
import PrimaryButton from "./shared/PrimaryButton";

// Download icon SVG
const DOWNLOAD_ICON = `<svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M15.111 11.357a.89.89 0 0 0-.889.888v2.134c0 .141-.056.276-.156.376s-.235.156-.376.156H2.31c-.141 0-.276-.056-.376-.156s-.156-.235-.156-.376v-2.134a.889.889 0 1 0-1.778 0v2.134c0 .612.244 1.2.677 1.633s1.021.677 1.633.677h11.378c.612 0 1.2-.244 1.633-.677s.677-1.021.677-1.633v-2.134a.89.89 0 0 0-.889-.888Z" fill="currentColor"/>
  <path d="M8 .69a.89.89 0 0 0-.889.889v8.057L4.302 7.296a.89.89 0 0 0-1.138 1.365l4.267 3.556c.044.032.092.06.142.083.032.022.066.041.101.058.103.043.213.065.324.065s.221-.022.324-.065c.034-.017.068-.036.101-.057.05-.023.098-.051.142-.083l4.267-3.556a.89.89 0 0 0-1.138-1.365L8.89 9.636V1.579A.89.89 0 0 0 8 .69Z" fill="currentColor"/>
</svg>`;

export interface BenefitData {
  icon: IconData;
  title: TextNodeType;
  amount: TextNodeType;
}

export interface InsuranceListItemProps {
  csrfToken?: string;
  quoteId?: string; // Add quoteId for filtering
  productId?: string; // Add productId for navigation
  logoUrl?: string;
  companyName?: TextNodeType;
  planTitle?: TextNodeType;
  originalPrice?: TextNodeType;
  discountedPrice?: TextNodeType;
  perPersonText?: TextNodeType;
  discountText?: TextNodeType;
  benefits?: BenefitData[];
  moreBenefitsText?: TextNodeType;
  onMoreBenefits?: () => void;
  onDownload?: (url: string) => void;
  buyButtonText?: TextNodeType;

  // New downloadBenefitsModal structure
  downloadBenefitsModal?: {
    header?: {
      title?: TextNodeType;
    };
    content?: {
      description?: TextNodeType;
      options?: {
        id?: string;
        label?: TextNodeType;
        defaultSelected?: boolean;
        templateType?: string; // Maps to "with_price" or "without_price"
      }[];
    };
    footer?: {
      buttons?: {
        id?: string;
        text?: TextNodeType;
        type?: string;
        buttonStyles?: {
          backgroundColor?: string;
          border?: string;
          padding?: string;
          borderRadius?: string;
        };
      }[];
    };
  };

  bottomsheet?: {
    benefitItemElementLayout?: {
      id?: string;
      icon?: string;
      title?: TextNodeType;
      info?: TextNodeType;
      price?: TextNodeType;
    };
    moreBenefits?: {
      [key: string]: IBenefits[] | undefined;
    };
    totalBenefits?: string;
    titleDownloadBenefit?: TextNodeType;
    disclaimer?: string;
    PowerBy?: TextNodeType;
    insPlanName?: TextNodeType;
    insProvider?: TextNodeType;
    tnc?: {
      title?: TextNodeType;
      link?: string;
    };
    downloadBenefitsDropdown?: {
      header?: {
        title?: TextNodeType;
      };
      options?: {
        id?: string;
        label?: TextNodeType;
        defaultSelected?: boolean;
        templateType?: string; // Maps to "with_price" or "without_price"
      }[];
    };
  };
  insAmount?: TextNodeType;
  paxPerDay?: TextNodeType;
  buyBtn?: string;
  moreBenefitsTitle?: string;

  // Pre-transformed MoreBenefits data to avoid inline transformation
  moreBenefitsData?: import("@tm/api-utils").CardDataInterface;
}

const InsuranceListItem: React.FC<InsuranceListItemProps> = ({
  quoteId,
  productId,
  logoUrl,
  companyName,
  planTitle,
  originalPrice,
  discountedPrice,
  perPersonText,
  discountText,
  benefits,
  moreBenefitsText,
  buyButtonText,
  onMoreBenefits,
  onDownload,
  csrfToken,

  downloadBenefitsModal,
  bottomsheet,
  insAmount,
  paxPerDay,
  buyBtn,
  moreBenefitsTitle = "More Benefits",
  moreBenefitsData,
}) => {
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isMoreBenefitsModalOpen, setIsMoreBenefitsModalOpen] = useState(false);
  const { showNetPrice: isNetPricing } = useNetPriceContext();
  const planTitleValue = getPlainText(planTitle);

  const analytics = getAnalytics();

  useEffect(() => {
    if (analytics && planTitleValue) {
      analytics?.trackListingEvents({
        actionType: `${planTitleValue}_seen`,
      });
      if (discountText) {
        analytics?.trackListingEvents({
          actionType: `commission_persuasion_shown_${planTitleValue}`,
        });
      }
    }
  }, []);

  const buyProduct = (
    quoteId: string | undefined,
    productId: string | undefined
  ) => {
    const searchParams = new URLSearchParams(window.location.search);
    const queryParams = Object.fromEntries(searchParams.entries());

    const params = {
      ...queryParams,
      countries: searchParams.get("countries"),
      startDate: searchParams.get("startDate"),
      endDate: searchParams.get("endDate"),
      travellers: searchParams.get("travellers"),
      isUpcomingTrip: searchParams.has("isUpcomingTrip")
        ? searchParams.get("isUpcomingTrip")
        : false,
      productId: productId || "",
      quoteId: quoteId || "",
    };

    navigationActions.navigateToTravellerDetails(params);
  };

  const handleDownloadClick = () => {
    analytics?.trackListingEvents({
      actionType: `download_benefits_clicked`,
    });
    setIsDownloadModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsDownloadModalOpen(false);
  };

  const handleMoreBenefitsClick = () => {
    analytics?.trackListingEvents({
      actionType: `View_details_clicked_${planTitleValue}`,
    });
    setIsMoreBenefitsModalOpen(true);
    onMoreBenefits?.();
  };

  const handleBuyButton = () => {
    analytics?.trackListingEvents({
      actionType: `buy_now_clicked_details_${planTitleValue}`,
    });
    buyProduct(quoteId, productId);
  };

  const handleCloseMoreBenefitsModal = () => {
    setIsMoreBenefitsModalOpen(false);
  };
  // Check if debug mode is enabled via Next.js environment variable
  const isDebugMode =
    typeof process !== "undefined" &&
    process?.env?.["MYPARTNER_FILTER_DEBUG"] === "true";

  return (
    <CardContainer style={{ position: "relative" }}>
      {/* Debug Quote ID Display */}
      {isDebugMode && quoteId && (
        <div
          style={{
            position: "absolute",
            top: "5px",
            right: "5px",
            backgroundColor: "#ff0000",
            color: "white",
            padding: "2px 6px",
            fontSize: "10px",
            borderRadius: "3px",
            zIndex: 10,
            fontFamily: "monospace",
          }}
        >
          {quoteId}
        </div>
      )}

      {/* Top Section: Logo + Title (Left) | Price + CTA (Right) */}
      <TopSection>
        <LeftSection>
          <LogoSection>
            <img
              src={logoUrl}
              alt="Insurance Company Logo"
              style={{ height: "40px" }}
            />
          </LogoSection>
          <TitleSection>
            <PlanTitle>
              <FormattedText value={planTitle} />
            </PlanTitle>
            <CompanyName>
              <FormattedText value={companyName} />
            </CompanyName>
          </TitleSection>
        </LeftSection>

        <RightSection>
          <div>
            <PriceContainer $isNetPricing={isNetPricing}>
              {isNetPricing ? (
                <>
                  {originalPrice && (
                    <CurrentPrice>
                      <FormattedText value={originalPrice} />
                    </CurrentPrice>
                  )}
                  <PerPersonText>
                    <FormattedText value={perPersonText} />
                  </PerPersonText>
                </>
              ) : (
                <>
                  {originalPrice && (
                    <StrikethroughPrice>
                      <FormattedText value={originalPrice} />
                    </StrikethroughPrice>
                  )}
                  <CurrentPrice>
                    <FormattedText value={discountedPrice} />
                  </CurrentPrice>
                  <PerPersonText>
                    <FormattedText value={perPersonText} />
                  </PerPersonText>
                </>
              )}
            </PriceContainer>
            {!isNetPricing && (
              <DiscountWrapper>
                <DiscountText>
                  <FormattedText value={discountText} />
                </DiscountText>
              </DiscountWrapper>
            )}
          </div>
          <PrimaryButton
            text={buyButtonText}
            onClick={() => {
              analytics?.trackListingEvents({
                actionType: `buy_now_clicked_outside_${planTitleValue}`,
              });
              buyProduct(quoteId, productId);
            }}
            fontSize={12}
          />
        </RightSection>
      </TopSection>

      {/* Bottom Section: Benefits (Left) | More Benefits + Download (Right) */}
      <BottomSection>
        <BenefitsSection>
          {benefits?.map((benefit, index) => (
            <BenefitItem
              key={`benefit-${benefit.icon.url || benefit.icon.svg || index}`}
            >
              <BenefitIcon>
                <Icon icon={benefit.icon} alt="Benefit icon" />
              </BenefitIcon>
              <BenefitText>
                <FormattedText value={benefit.title} />
                <FormattedText value={benefit.amount} />
              </BenefitText>
            </BenefitItem>
          ))}
        </BenefitsSection>

        <ActionsSection>
          {moreBenefitsData && (
            <MoreBenefitsSection onClick={handleMoreBenefitsClick}>
              <FormattedText value={moreBenefitsText} />
            </MoreBenefitsSection>
          )}
          <DownloadSection onClick={handleDownloadClick}>
            <DownloadIcon>
              <Icon icon={{ svg: DOWNLOAD_ICON }} alt="Download" />
            </DownloadIcon>
          </DownloadSection>
        </ActionsSection>
      </BottomSection>

      {/* Download Policy Modal */}
      <DownloadPolicyModal
        data={downloadBenefitsModal}
        quoteId={quoteId}
        isOpen={isDownloadModalOpen}
        onClose={handleCloseModal}
        csrfToken={csrfToken}
      />

      {/* More Benefits Modal */}
      {isMoreBenefitsModalOpen && moreBenefitsData && (
        <MoreBenefits
          data={{ ...moreBenefitsData, csrfToken: csrfToken }}
          moreBenfitsTitle={moreBenefitsTitle}
          handleClose={handleCloseMoreBenefitsModal}
          handleBuyButton={handleBuyButton}
        />
      )}
    </CardContainer>
  );
};

export default InsuranceListItem;
